import { html, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import store from '../../../store/store';
import { StoreState } from '../../../store/store.interface';
import { formatDateDefault, formattedAuthors, ordinalSuffixOf } from '../../../common/utils/util';
import { getCartItemFormat } from '../../../common/utils/cart-util';
import {
  appendDurationForTunnelPurchaseFlow,
  appendedDurationForHEProducts,
  calculateDurationDigitalProduct,
  getImageSrcUrl,
  isSharpenPlus,
} from '../../../common/utils/product-util';
import {
  AddressType,
  OrderHistory as IOrderHistory,
  ProductType,
  UserOrderDetails,
} from '../../order-history/order-history.interface';
import { OrderHistory } from '../../order-history/order-history';
import { OfferTypeChecker } from '../../../common/utils/offer-type-util';
import { getBadgeStyleFromLineStatusItem, getOrderUiStatus } from '../../order-history/utils/order-history.util';
import { getAccessType, renderAccessMessage } from '../../../common/utils/access-message-util';
import { SampleHistoryAnalytics } from './sample-history.analytic';
import { fetchInstructorSampleOrders } from '../thunks/fetch-instructor-sample-orders.thunk';
import { getInstructorSampleOrdersData, getInstructorSampleOrdersStatus } from '../store/my-account.selector';

const INSTRUCTOR_COMP = 'INSTRUCTOR_COMP';

@customElement('mhecomm-sample-history')
export class SampleHistory extends OrderHistory {
  protected fetchOrders(forceUpdate = false) {
    store.dispatch(fetchInstructorSampleOrders({ ...(forceUpdate && { forceUpdate: true }) }));
  }

  protected getOrdersHistoryData(state: StoreState) {
    return getInstructorSampleOrdersData(state);
  }

  protected getOrdersHistoryStatus(state: StoreState) {
    return getInstructorSampleOrdersStatus(state);
  }

  renderContent() {
    const totalOrders = this.ordersHistory!.length;
    const descriptionHowManyOrders = totalOrders > 1 ? 'orders' : 'order';

    return totalOrders === 0
      ? html`<p class="fs-3">You have no sample history at this time.</p>`
      : html`
          <div class="row justify-content-sm-center">
            <div class="col-12">
              <div class="fw-light">
                <p>${totalOrders} ${descriptionHowManyOrders} found.</p>
              </div>
              ${this.ordersHistory?.map(
                (order, index) => html`
                  <div class="card text-left rounded-3 ${index > 0 ? 'mt-5' : ''}">
                    <div class="card-header py-3">
                      <div class="lh-1">
                        <div class="row">
                          <div class="col">
                            <h2 class="h4 fs-large fs-sm-4 mb-1">Order Placed: ${formatDateDefault(order.orderedDate)}</h2>
                          </div>
                        </div>
                        <div class="row">
                          <div class="col">
                            <div class="lh-base fs-small fs-sm-6">
                              <span>Order Number:</span>
                              <span class="fw-bold" data-automation-id="order_${order.webOrderNo}">${order.webOrderNo}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="card-body p-0">
                      <ul class="list-group">
                        ${order.userOrderDetails.map(
                          (item) =>
                            OfferTypeChecker.isCreateCustom(item.offerType)
                              ? html`
                                  <li class="d-block">
                                    <mhecomm-create-custom-order-item-display
                                      .cta="${this._triggerCTAAnalyticsData.bind(this)}"
                                      .item="${{ ...item, productDescription: this.getTitle(item) }}"
                                      .order="${order}"
                                    >
                                    </mhecomm-create-custom-order-item-display>
                                  </li>
                                `
                              : html`
                                  <li class="d-block">
                                    <div class="row p-3">
                                      <div class="col-4 col-sm-3 col-md-2">
                                        <img
                                          alt=${item.productDescription}
                                          class="img-fluid"
                                          src=${getImageSrcUrl(item.coverImage ? item.coverImage : item.productIsbn)}
                                        />
                                      </div>
                                      <div class="col-8 col-sm-9 col-md-4">
                                        <h3 class="h4 fs-large fw-semibold">
                                          <span data-automation-id="item-title-${order.webOrderNo}-${item.isbn10}">${
                                            this.getTitle(item)
                                          }</span>
                                          <br />
                                          ${!item.freeTrialInfo?.isFreeTrial
                                            ? html`
                                                <span class="badge mt-2 ${getBadgeStyleFromLineStatusItem(order, item)}">
                                                  ${getOrderUiStatus(order, item)}
                                                </span>
                                              `
                                            : nothing}
                                        </h3>

                                        ${!isSharpenPlus(item.programSourceId)
                                          ? html`
                                              <div class="d-none d-sm-block">
                                                <span class="fw-bold">${
                                                  item.productSegment === 'SEG' ? 'MHID:' : 'ISBN10:'
                                                }</span>
                                                ${item.productSegment === 'SEG' ? item.mhid : item.isbn10}
                                                ${item.isbn13
                                                  ? html`
                                                      |
                                                      <span class="fw-bold">ISBN13:</span>
                                                      ${item.isbn13}
                                                    `
                                                  : ''}
                                              </div>
                                              <div class="d-sm-none">
                                                <span class="fw-bold">${
                                                  item.productSegment === 'SEG' ? 'MHID:' : 'ISBN10:'
                                                }</span>
                                                ${item.productSegment === 'SEG' ? item.mhid : item.isbn10}
                                                ${item.isbn13 ? '|' : ''}
                                              </div>
                                              ${item.isbn13
                                                ? html`
                                                    <div class="d-sm-none">
                                                      <span class="fw-bold">ISBN13:</span>
                                                      ${item.isbn13}
                                                    </div>
                                                  `
                                                : ''}
                                            `
                                          : nothing}
                                        ${!isSharpenPlus(item.programSourceId) && item.authors && !item.freeTrialInfo?.isFreeTrial
                                          ? html` <div><span class="fw-bold">By:</span> ${formattedAuthors(item.authors)}</div>`
                                          : ''}
                                        ${!isSharpenPlus(item.programSourceId) && item.copyrightYear && !item.isEvergreen
                                          ? html`<div>©${item.copyrightYear}</div>`
                                          : ''}
                                        ${item.formatDescription
                                          ? html`
                                              <div>
                                                <span class="fw-bold">Format:</span>
                                                ${getCartItemFormat(item.formatDescription, item.formatCode, item.virtual)}
                                              </div>
                                            `
                                          : ''}
                                        ${isSharpenPlus(item.programSourceId)
                                          ? html`
                                              <div class="mt-2 mt-md-3 badge bg-info text-black">
                                                Expires on
                                                ${calculateDurationDigitalProduct(
                                                  Number(item.extension_attributes?.duration),
                                                  item.extension_attributes?.duration_unit,
                                                  item.createTime
                                                ).toLocaleDateString('en-US')}
                                              </div>
                                            `
                                          : nothing}
                                      </div>
                                      <div class="col-12 col-sm-12 col-md-3">
                                        <div class="row">
                                          ${!item.freeTrialInfo?.isFreeTrial && !isSharpenPlus(item.programSourceId)
                                            ? html`
                                                <div class="offset-md-0 offset-sm-3 offset-4 col-4 col-sm-4 col-md-12 col-lg-12">
                                                  <div class="fw-semibold my-md-0 my-3">
                                                    Quantity:&nbsp;${item.itemQuantity}
                                                  </div>
                                                </div>
                                              `
                                            : nothing}
                                        </div>
                                      </div>
                                      <div class="col-12 col-md-3 text-md-end px-2 px-md-0 px-xl-2">
                                        ${this.displayAccessOrLaunchButton(order, item)} ${this.launchSharpen(order, item)}
                                      </div>
                                    </div>
                                    <hr class="hr-primary m-0" aria-hidden="true" />
                                  </li>
                                `
                        )}
                      </ul>
                      ${this._isPhysicalProduct(order)
                        ? html`
                            <div class="row p-3">
                              <div>
                                <button
                                  aria-label="View Order Details for ${order.webOrderNo}"
                                  class="btn link-primary btn-sm link-collapse px-2 collapsed"
                                  data-bs-toggle="collapse"
                                  data-bs-target="#orderHistoryCollapse${order.webOrderNo}"
                                  aria-expanded="false"
                                  aria-controls="orderHistoryCollapse${order.webOrderNo}"
                                  @click=${(event) => this._handleCollapseToggle(order, event)}
                                  id="orderDetailsLink${order.webOrderNo}"
                                >
                                  View Order Details${this.uxtIconReady
                                    ? html`<uxt-icon id="icon${order.webOrderNo}" class="ms-1" icon="chevron-down-sm"></uxt-icon>`
                                    : ''}
                                </button>
                              </div>
                            </div>
                            <div id="orderHistoryCollapse${order.webOrderNo}" class="row p-3 pt-0 collapse">
                              <p class="fw-bold">Shipping information: ${order.formatDescription}</p>
                              ${order.shippingAddress && this._displayShippingAddress(order, undefined)
                                ? html`<div class="col-12 col-sm-6">
                                    <div class="fw-bold">Shipped To:</div>
                                    <div class="mt-2">${order.shippingAddress?.firstName} ${order.shippingAddress?.lastName}</div>
                                    <div>${order.shippingAddress?.street1}</div>
                                    <div>
                                      ${order.shippingAddress?.city}, ${order.shippingAddress?.state}
                                      ${order.shippingAddress?.zipCode}
                                    </div>
                                    ${order.shippingAddress.phone
                                      ? html`<div>Phone: ${order.shippingAddress?.phone}</div>`
                                      : nothing}
                                  </div>`
                                : nothing}
                              ${order.billingAddress && this._displayBillingAddress(order, undefined)
                                ? html`<div class="col-12 col-sm-6 mt-4 mt-sm-0">
                                    <div class="fw-bold">Billed To:</div>
                                    <div class="mt-2">${order.billingAddress?.firstName} ${order.billingAddress?.lastName}</div>
                                    <div>${order.billingAddress?.street1}</div>
                                    <div>
                                      ${order.billingAddress?.city}, ${order.billingAddress?.state}
                                      ${order.billingAddress?.zipCode}
                                    </div>
                                    <div>Phone: <span>${order.billingAddress?.phone}</span></div>
                                    <div class="mt-2"><span class="fw-bold">Payment:</span> ${order.paymentType}</div>
                                  </div>`
                                : nothing}
                            </div>
                          `
                        : nothing}
                    </div>
                  </div>
                `
              )}
            </div>
          </div>
        `;
  }

  private displayAccessOrLaunchButton(order: IOrderHistory, item: UserOrderDetails) {
    const accessType = getAccessType(item);
    return accessType ? renderAccessMessage(accessType) : this._getButtonFromUserOrderDetails(order, item);
  }

  _denormalizeOrderHistory(orders: IOrderHistory[]): IOrderHistory[] {
    return orders
      .filter((order) => !order.webOrderNo.startsWith('CODE_'))
      .filter((order) => this.isInstructorSampleOrder(order))
      .map((order) => ({
        ...order,
        billingAddress: order.address.find((address) => address.type === AddressType.BILLING_ADDRESS),
        shippingAddress: order.address.find((address) => address.type === AddressType.SHIPPING_ADDRESS),
      }))
      .sort((a, b) => new Date(b.orderedDate).getTime() - new Date(a.orderedDate).getTime());
  }

  _isTrackOrder(orderDetail: UserOrderDetails) {
    return !this._isBookshelf(orderDetail) && orderDetail.productType === ProductType.SHIPPABLE;
  }

  async _orderHistoryAnalyticsData(orders: IOrderHistory[]) {
    SampleHistoryAnalytics.load({ element: this, orders });
  }

  _viewOrderDetailsAnalyticsData(order: IOrderHistory) {
    SampleHistoryAnalytics.viewOrderDetails({ element: this, order });
  }

  _triggerCTAAnalyticsData(name: string, label: string, order: IOrderHistory, item: UserOrderDetails, href?: string) {
    SampleHistoryAnalytics.cta({ element: this, name, label, order, item, href });
  }

  private isInstructorSampleOrder(order: IOrderHistory): boolean {
    return order.orderType?.toUpperCase() === INSTRUCTOR_COMP;
  }

  getTitle(item: UserOrderDetails) {
    return `${item.productDescription}
      ${item.edition && parseInt(item.edition) > 0 && !item.isEvergreen ? `, ${ordinalSuffixOf(item.edition)} Edition` : ''}
      ${appendedDurationForHEProducts({
        businessUnit: item?.businessUnit!,
        isVirtual: item.virtual,
        formatTypeDesc: item?.formatTypeDescription!,
        duration: item?.duration!,
        durationPeriod: item?.durationPeriod!,
        ...(item?.offerType && { offerType: item?.offerType }),
      })}
      ${item.isConnectSharpenBundle ? ' (Connect with Sharpen)' : ''}
      ${!item.duration && !item.durationPeriod && item.terms
        ? appendDurationForTunnelPurchaseFlow({
            term: item.terms,
          })
        : ''}`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-sample-history': SampleHistory;
  }
}
